import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, ActivityIndicator, Platform, LayoutAnimation, UIManager, TextInput, Switch } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useAppStore } from '../store/appStore';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { apiService, BusinessUser } from '../services/api';
import { Header, ScreenLayout, ConfirmationModal, BusinessUserPicker, PermissionLevelPicker } from '../components';
import {
  PermissionLevel,
  PermissionCatalogItem,
  BusinessPermissionProfile,
  isValidLevel,
} from '../constants/permissionLevels';
import { useThemedStyles } from '../hooks/useThemedStyles';

if (Platform.OS === 'android' && UIManager.setLayoutAnimationEnabledExperimental) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

interface Department {
  _id: string;
  name: string;
}

type TargetType = 'user' | 'department';

interface BusinessPermissionsScreenProps {
  targetType?: TargetType;
  targetId?: string;
  targetIds?: string[];
}

const CATEGORY_ICON: Record<string, string> = {
  general: 'settings-outline',
  wabu: 'logo-whatsapp',
  fbm: 'chatbubbles-outline',
  sms: 'chatbox-outline',
  email: 'mail-outline',
  telegram: 'paper-plane-outline',
  instagram: 'camera-outline',
  contacts: 'people-outline',
  reports: 'bar-chart-outline',
  phone: 'call-outline',
  notifications: 'notifications-outline',
  storage: 'cloud-outline',
  visits: 'walk-outline',
  task: 'document-text-outline',
};

// Admin/security-critical general permissions shown first, under "System permissions".
const SYSTEM_PERMISSION_KEYS = new Set([
  'general.manageUsers',
  'general.manageDepartments',
  'general.managePermissions',
  'general.manageChannelsConnections',
  'general.manageIntegrationsApiKeys',
  'general.manageSubscriptionBilling',
  'general.editBusinessProfile',
  'general.exportBusinessData',
]);

// Services accordion order — Contacts/WhatsApp/Messenger first, everything else keeps
// its existing relative order after that. "notifications" is intentionally absent: its
// permissions render inside the "Other" general subsection instead of their own section.
const SERVICE_ORDER = ['contacts', 'wabu', 'fbm', 'visits'];
const sortServiceKeys = (keys: string[]): string[] => {
  const prioritized = SERVICE_ORDER.filter(key => keys.includes(key));
  const rest = keys.filter(key => !SERVICE_ORDER.includes(key) && key !== 'notifications');
  return [...prioritized, ...rest];
};

const CollapsibleSection: React.FC<{
  title: string;
  icon: string;
  summaryText?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  allEnabled?: boolean;
  onToggleAll?: (enable: boolean) => void;
}> = ({ title, icon, summaryText, children, defaultOpen = false, allEnabled, onToggleAll }) => {
  const styles = useThemedStyles(getStyles);
  const [open, setOpen] = useState(defaultOpen);
  const rtl = useRTLStyles();

  const toggle = () => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setOpen(prev => !prev);
  };

  return (
    <View style={styles.section}>
      <TouchableOpacity style={[styles.sectionHeader, { flexDirection: rtl.rowDirection }]} onPress={toggle} activeOpacity={0.7}>
        <View style={[styles.sectionHeaderLeft, { flexDirection: rtl.rowDirection }]}>
          <Ionicons name={icon as any} size={18} color={colors.primary} />
          <Text style={styles.sectionTitle}>{title}</Text>
        </View>
        <View style={[styles.sectionHeaderRight, { flexDirection: rtl.rowDirection }]}>
          {summaryText && <Text style={styles.sectionSummary}>{summaryText}</Text>}
          {onToggleAll && (
            <View onStartShouldSetResponder={() => true}>
              <Switch
                value={!!allEnabled}
                onValueChange={(value) => onToggleAll(value)}
                trackColor={{ false: colors.border, true: colors.primary }}
                thumbColor={colors.white}
              />
            </View>
          )}
          <Ionicons name={open ? 'chevron-up' : 'chevron-down'} size={18} color={colors.text.secondary} />
        </View>
      </TouchableOpacity>
      {open && <View style={styles.sectionBody}>{children}</View>}
    </View>
  );
};

export const BusinessPermissionsScreen: React.FC<BusinessPermissionsScreenProps> = ({ targetType: initialTargetType, targetId: initialTargetId, targetIds: initialTargetIds }) => {
  const styles = useThemedStyles(getStyles);
  const { showNotification, navigateTo, setActiveTab, activeTab } = useAppContext();
  const { currentBusiness } = useAppStore();
  const { t } = useLocale();
  const rtl = useRTLStyles();

  const isBulkMode = Boolean(initialTargetIds && initialTargetIds.length > 0);

  const [targetType, setTargetType] = useState<TargetType>(initialTargetType || 'user');
  const [targetId, setTargetId] = useState<string | undefined>(initialTargetId);
  const [targetIds] = useState<string[] | undefined>(initialTargetIds);
  const [showTargetPicker, setShowTargetPicker] = useState(!initialTargetId && !isBulkMode);

  const [targetLabel, setTargetLabel] = useState<string>('');
  const [catalog, setCatalog] = useState<{ general: PermissionCatalogItem[]; services: Record<string, PermissionCatalogItem[]> }>({ general: [], services: {} });
  const [profile, setProfile] = useState<BusinessPermissionProfile | null>(null);
  const [effectiveDepartmentUnion, setEffectiveDepartmentUnion] = useState<Record<string, PermissionLevel>>({});
  const [draftGrants, setDraftGrants] = useState<Record<string, PermissionLevel>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingNavAction, setPendingNavAction] = useState<(() => void) | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    setActiveTab('departments');
  }, [setActiveTab]);

  // Load catalog once per business
  useEffect(() => {
    if (!currentBusiness?._id) return;
    apiService.getPermissionCatalog(currentBusiness._id).then(response => {
      if (response.statusCode === 1000 && response.data) {
        setCatalog(response.data);
      }
    }).catch(err => console.error('❌ [BusinessPermissionsScreen] Error loading catalog:', err));
  }, [currentBusiness?._id]);

  const allItems = useMemo(() => [catalog.general, ...Object.values(catalog.services)].flat(), [catalog]);

  const matchesSearch = useCallback((item: PermissionCatalogItem) => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;
    const label = t(item.labelKey as any).toLowerCase();
    const description = t(item.descriptionKey as any).toLowerCase();
    return label.includes(query) || description.includes(query) || item.key.toLowerCase().includes(query);
  }, [searchQuery, t]);

  // "Other" also absorbs the Notifications service's items, so they render under the
  // general section instead of their own services accordion — see SERVICE_ORDER above.
  const filteredSystemGeneral = useMemo(
    () => catalog.general.filter(item => SYSTEM_PERMISSION_KEYS.has(item.key)).filter(matchesSearch),
    [catalog.general, matchesSearch]
  );
  const filteredOtherGeneral = useMemo(() => {
    const otherGeneral = catalog.general.filter(item => !SYSTEM_PERMISSION_KEYS.has(item.key));
    const notifications = catalog.services.notifications || [];
    return [...otherGeneral, ...notifications].filter(matchesSearch);
  }, [catalog.general, catalog.services.notifications, matchesSearch]);

  const filteredServices = useMemo(() => {
    const result: Record<string, PermissionCatalogItem[]> = {};
    for (const serviceKey of sortServiceKeys(Object.keys(catalog.services))) {
      const filtered = catalog.services[serviceKey].filter(matchesSearch);
      if (filtered.length > 0) result[serviceKey] = filtered;
    }
    return result;
  }, [catalog.services, matchesSearch]);

  const isGroupFullyEnabled = useCallback((items: PermissionCatalogItem[]) => {
    if (items.length === 0) return false;
    return items.every(item => {
      const explicitLevel = draftGrants[item.key];
      const level = isValidLevel(explicitLevel)
        ? explicitLevel
        : (isValidLevel(effectiveDepartmentUnion[item.key]) ? effectiveDepartmentUnion[item.key] : 'none');
      const topLevel = item.levels[item.levels.length - 1];
      return level === topLevel;
    });
  }, [draftGrants, effectiveDepartmentUnion]);

  const handleToggleGroup = (items: PermissionCatalogItem[], enable: boolean) => {
    setDraftGrants(prev => {
      const next = { ...prev };
      for (const item of items) {
        next[item.key] = enable ? item.levels[item.levels.length - 1] : 'none';
      }
      return next;
    });
  };

  // Load the target's own profile + (for users) their department-union, for the "inherited" state
  const loadTargetData = useCallback(async () => {
    if (!currentBusiness?._id || !targetId) return;
    try {
      setLoading(true);
      const profileResponse = targetType === 'user'
        ? await apiService.getUserPermissionProfile(currentBusiness._id, targetId)
        : await apiService.getDepartmentPermissionProfile(currentBusiness._id, targetId);

      const loadedProfile = profileResponse.statusCode === 1000 ? profileResponse.data : null;
      setProfile(loadedProfile || null);
      setDraftGrants(loadedProfile?.grants || {});

      if (targetType === 'user') {
        const effectiveResponse = await apiService.authenticatedRequest<Record<string, PermissionLevel>>(
          `/api/business-permissions/${currentBusiness._id}/users/${targetId}/effective`,
          { method: 'GET' }
        );
        if (effectiveResponse.statusCode === 1000 && effectiveResponse.data) {
          // Department-union-only values are whatever's in "effective" but NOT explicitly
          // set on this user's own profile — used to render the inherited/dashed state.
          setEffectiveDepartmentUnion(effectiveResponse.data);
        }
      } else {
        setEffectiveDepartmentUnion({});
      }
    } catch (error: any) {
      console.error('❌ [BusinessPermissionsScreen] Error loading target permissions:', error);
      showNotification('error', t('common.error'), t('permissions.saveFailed'));
    } finally {
      setLoading(false);
    }
  }, [currentBusiness?._id, targetId, targetType]);

  useEffect(() => {
    if (targetId && !isBulkMode) {
      loadTargetData();
    } else if (isBulkMode) {
      setDraftGrants({});
    }
  }, [targetId, isBulkMode, loadTargetData]);

  // Resolve a display label for the single-target header row
  useEffect(() => {
    if (!targetId || !currentBusiness?._id) {
      setTargetLabel('');
      return;
    }
    if (targetType === 'user') {
      apiService.getBusinessUsers(currentBusiness._id).then(response => {
        if (response.statusCode === 1000 && response.data) {
          const found = response.data.find((u: BusinessUser) => u.userId === targetId);
          if (found) {
            setTargetLabel(`${found.user?.firstName || ''} ${found.user?.lastName || ''}`.trim() || found.user?.email || targetId);
          }
        }
      }).catch(() => {});
    } else {
      apiService.getDepartments(currentBusiness._id).then(response => {
        if (response.statusCode === 1000 && response.data) {
          const found = response.data.find((d: Department) => d._id === targetId);
          if (found) setTargetLabel(found.name);
        }
      }).catch(() => {});
    }
  }, [targetId, targetType, currentBusiness?._id]);

  const hasUnsavedChanges = useMemo(() => {
    const currentGrants = profile?.grants || {};
    const keys = new Set([...Object.keys(draftGrants), ...Object.keys(currentGrants)]);
    for (const key of keys) {
      if (draftGrants[key] !== currentGrants[key]) return true;
    }
    return false;
  }, [draftGrants, profile]);

  const handleTargetPickerConfirm = (selectedIds: string[]) => {
    if (selectedIds[0]) {
      setTargetId(selectedIds[0]);
      setShowTargetPicker(false);
    }
  };

  const handleLevelChange = (itemKey: string, level: PermissionLevel) => {
    setDraftGrants(prev => ({ ...prev, [itemKey]: level }));
  };

  const handleResetItemToInherited = (itemKey: string) => {
    setDraftGrants(prev => {
      const next = { ...prev };
      delete next[itemKey];
      return next;
    });
  };

  const handleSave = async () => {
    if (!currentBusiness?._id) return;
    try {
      setSaving(true);
      if (isBulkMode && targetIds) {
        const targets = targetIds.map(id => ({ targetType, targetId: id }));
        const response = await apiService.bulkSetPermissionProfiles(currentBusiness._id, targets, draftGrants);
        if (response.statusCode === 1000) {
          showNotification('success', t('common.success'), t('permissions.saveSuccess'));
          navigateTo(targetType === 'user' ? 'user-setup' : 'departments');
        } else {
          showNotification('error', t('common.error'), response.errors?.[0]?.message || t('permissions.saveFailed'));
        }
      } else if (targetId) {
        const response = targetType === 'user'
          ? await apiService.setUserPermissionProfile(currentBusiness._id, targetId, draftGrants)
          : await apiService.setDepartmentPermissionProfile(currentBusiness._id, targetId, draftGrants);
        if (response.statusCode === 1000) {
          showNotification('success', t('common.success'), t('permissions.saveSuccess'));
          setProfile(response.data || null);
        } else {
          showNotification('error', t('common.error'), response.errors?.[0]?.message || t('permissions.saveFailed'));
        }
      }
    } catch (error: any) {
      console.error('❌ [BusinessPermissionsScreen] Error saving permissions:', error);
      showNotification('error', t('common.error'), t('permissions.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (hasUnsavedChanges) {
      setPendingNavAction(() => () => navigateTo(targetType === 'user' ? 'user-setup' : 'departments'));
      setShowUnsavedModal(true);
    } else {
      navigateTo(targetType === 'user' ? 'user-setup' : 'departments');
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const renderItemRow = (item: PermissionCatalogItem) => {
    const explicitLevel = draftGrants[item.key];
    const isInherited = !isValidLevel(explicitLevel);
    const displayLevel: PermissionLevel = isInherited
      ? (isValidLevel(effectiveDepartmentUnion[item.key]) ? effectiveDepartmentUnion[item.key] : 'none')
      : explicitLevel;

    return (
      <View key={item.key} style={[styles.itemRow, { flexDirection: rtl.rowDirection }]}>
        <View style={styles.itemInfo}>
          <Text style={[styles.itemLabel, { textAlign: rtl.textStart }]}>{t(item.labelKey as any)}</Text>
          <Text style={[styles.itemDescription, { textAlign: rtl.textStart }]}>{t(item.descriptionKey as any)}</Text>
        </View>
        <View style={[styles.itemControls, { flexDirection: rtl.rowDirection }]}>
          {!isInherited && targetType === 'user' && (
            <TouchableOpacity onPress={() => handleResetItemToInherited(item.key)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Ionicons name="refresh-outline" size={16} color={colors.text.secondary} />
            </TouchableOpacity>
          )}
          <PermissionLevelPicker
            levels={item.levels}
            value={displayLevel}
            inherited={isInherited}
            onChange={(level) => handleLevelChange(item.key, level)}
          />
        </View>
      </View>
    );
  };

  const showTargetSection = showTargetPicker && !isBulkMode;

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      activeSettingsItemId="team"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      rightPaneContent="empty"
    >
      <View style={styles.container}>
        <View style={[styles.topBar, { flexDirection: rtl.rowDirection }]}>
          <TouchableOpacity onPress={handleBack} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
            <Ionicons name={rtl.isRTL ? 'arrow-forward' : 'arrow-back'} size={22} color={colors.text.primary} />
          </TouchableOpacity>
          <Text style={styles.topBarTitle}>{t('permissions.screenTitle')}</Text>
          <View style={{ width: 22 }} />
        </View>

        {showTargetSection ? (
          <View style={styles.targetPickerContainer}>
            <View style={[styles.targetTypeTabs, { flexDirection: rtl.rowDirection }]}>
              <TouchableOpacity
                style={[styles.targetTab, targetType === 'user' && styles.targetTabActive]}
                onPress={() => setTargetType('user')}
              >
                <Text style={[styles.targetTabText, targetType === 'user' && styles.targetTabTextActive]}>{t('permissions.targetPicker.usersTab')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.targetTab, targetType === 'department' && styles.targetTabActive]}
                onPress={() => setTargetType('department')}
              >
                <Text style={[styles.targetTabText, targetType === 'department' && styles.targetTabTextActive]}>{t('permissions.targetPicker.departmentsTab')}</Text>
              </TouchableOpacity>
            </View>
            <Text style={styles.targetPickerHint}>{t('permissions.targetPicker.selectTarget')}</Text>
            <BusinessUserPicker
              visible={true}
              onClose={() => {}}
              onConfirm={handleTargetPickerConfirm}
              mode="single"
              source={targetType === 'user' ? 'users' : 'departments'}
              showAsModal={false}
            />
          </View>
        ) : (
          <ScrollView style={styles.scrollContainer} contentContainerStyle={styles.scrollContent}>
            {!isBulkMode && targetId && (
              <View style={[styles.editingForRow, { flexDirection: rtl.rowDirection }]}>
                <Text style={styles.editingForText}>
                  {t('permissions.targetPicker.editingFor', { name: targetLabel || targetId })}
                </Text>
                <TouchableOpacity onPress={() => setShowTargetPicker(true)}>
                  <Text style={styles.changeLink}>{t('permissions.targetPicker.change')}</Text>
                </TouchableOpacity>
              </View>
            )}

            <View style={styles.searchContainer}>
              <Ionicons name="search-outline" size={16} color={colors.text.secondary} />
              <TextInput
                style={[styles.searchInput, { textAlign: rtl.textStart }]}
                value={searchQuery}
                onChangeText={setSearchQuery}
                placeholder={t('permissions.search.placeholder')}
                placeholderTextColor={colors.text.secondary}
              />
              {searchQuery.length > 0 && (
                <TouchableOpacity onPress={() => setSearchQuery('')} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                  <Ionicons name="close-circle" size={16} color={colors.text.secondary} />
                </TouchableOpacity>
              )}
            </View>

            {loading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={colors.primary} />
              </View>
            ) : filteredSystemGeneral.length === 0 && filteredOtherGeneral.length === 0 && Object.keys(filteredServices).length === 0 ? (
              <View style={styles.loadingContainer}>
                <Text style={styles.itemDescription}>{t('permissions.search.noResults')}</Text>
              </View>
            ) : (
              <>
                {filteredSystemGeneral.length > 0 && (
                  <CollapsibleSection
                    title={t('permissions.systemSection.title')}
                    icon={CATEGORY_ICON.general}
                    defaultOpen
                    allEnabled={isGroupFullyEnabled(filteredSystemGeneral)}
                    onToggleAll={(enable) => handleToggleGroup(filteredSystemGeneral, enable)}
                  >
                    {filteredSystemGeneral.map(renderItemRow)}
                  </CollapsibleSection>
                )}

                {filteredOtherGeneral.length > 0 && (
                  <CollapsibleSection
                    title={t('permissions.otherSection.title')}
                    icon="ellipsis-horizontal-circle-outline"
                    defaultOpen={searchQuery.trim().length > 0}
                    allEnabled={isGroupFullyEnabled(filteredOtherGeneral)}
                    onToggleAll={(enable) => handleToggleGroup(filteredOtherGeneral, enable)}
                  >
                    {filteredOtherGeneral.map(renderItemRow)}
                  </CollapsibleSection>
                )}

                {Object.entries(filteredServices).map(([serviceKey, items]) => (
                  <CollapsibleSection
                    key={serviceKey}
                    title={t(`permissions.categoryLabel.${serviceKey}` as any)}
                    icon={CATEGORY_ICON[serviceKey] || 'apps-outline'}
                    defaultOpen={searchQuery.trim().length > 0}
                    allEnabled={isGroupFullyEnabled(items)}
                    onToggleAll={(enable) => handleToggleGroup(items, enable)}
                  >
                    {items.map(renderItemRow)}
                  </CollapsibleSection>
                ))}
              </>
            )}
          </ScrollView>
        )}

        {!showTargetSection && (
          <View style={[styles.footer, { flexDirection: rtl.rowDirection }]}>
            <TouchableOpacity style={styles.cancelButton} onPress={handleBack}>
              <Text style={styles.cancelButtonText}>{t('permissions.cancel')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.saveButton, (saving || !hasUnsavedChanges) && styles.saveButtonDisabled]}
              onPress={handleSave}
              disabled={saving || !hasUnsavedChanges}
            >
              {saving ? <ActivityIndicator size="small" color={colors.white} /> : <Text style={styles.saveButtonText}>{t('permissions.save')}</Text>}
            </TouchableOpacity>
          </View>
        )}
      </View>

      <ConfirmationModal
        visible={showUnsavedModal}
        onClose={() => setShowUnsavedModal(false)}
        onConfirm={() => {
          setShowUnsavedModal(false);
          pendingNavAction?.();
        }}
        title={t('permissions.unsavedChangesWarning.title')}
        message={t('permissions.unsavedChangesWarning.message')}
        confirmText={t('common.confirm')}
        cancelText={t('common.cancel')}
        type="warning"
      />
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  topBar: {
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    backgroundColor: colors.background.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  topBarTitle: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
  },
  targetPickerContainer: {
    flex: 1,
    padding: spacing.lg,
  },
  targetTypeTabs: {
    backgroundColor: colors.background.secondary,
    borderRadius: designTokens.borderRadius.md,
    padding: 3,
    marginBottom: spacing.md,
  },
  targetTab: {
    flex: 1,
    paddingVertical: spacing.sm,
    alignItems: 'center',
    borderRadius: designTokens.borderRadius.sm,
  },
  targetTabActive: {
    backgroundColor: colors.background.surface,
    ...designTokens.shadows.sm,
  },
  targetTabText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.secondary,
  },
  targetTabTextActive: {
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  targetPickerHint: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    marginBottom: spacing.md,
  },
  scrollContainer: {
    flex: 1,
  },
  scrollContent: {
    padding: spacing.lg,
    paddingBottom: 120,
  },
  editingForRow: {
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.background.secondary,
    borderRadius: designTokens.borderRadius.md,
    padding: spacing.md,
    marginBottom: spacing.md,
  },
  editingForText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.medium,
    flex: 1,
  },
  changeLink: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  loadingContainer: {
    paddingVertical: spacing.xl * 2,
    alignItems: 'center',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    paddingHorizontal: spacing.md,
    marginBottom: spacing.md,
    gap: spacing.sm,
  },
  searchInput: {
    flex: 1,
    paddingVertical: spacing.sm + 2,
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
  },
  section: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.md,
    marginBottom: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
    overflow: 'hidden',
  },
  sectionHeader: {
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
  },
  sectionHeaderLeft: {
    alignItems: 'center',
    gap: spacing.sm,
  },
  sectionHeaderRight: {
    alignItems: 'center',
    gap: spacing.sm,
  },
  sectionTitle: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
  },
  sectionSummary: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
  },
  sectionBody: {
    borderTopWidth: 1,
    borderTopColor: colors.border,
    paddingHorizontal: spacing.md,
  },
  itemRow: {
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    gap: spacing.md,
  },
  itemInfo: {
    flex: 1,
  },
  itemLabel: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
  },
  itemDescription: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
    marginTop: 2,
  },
  itemControls: {
    alignItems: 'center',
    gap: spacing.sm,
  },
  footer: {
    padding: spacing.md,
    gap: spacing.md,
    backgroundColor: colors.background.surface,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  cancelButton: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: spacing.md,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  cancelButtonText: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.medium,
  },
  saveButton: {
    flex: 2,
    alignItems: 'center',
    paddingVertical: spacing.md,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.primary,
  },
  saveButtonDisabled: {
    opacity: 0.5,
  },
  saveButtonText: {
    color: colors.white,
    fontFamily: typography.fontFamily.semibold,
  },
});
